import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Dropdown, Menu } from 'antd';
import SkeletonButton from 'antd/lib/skeleton/Button';
import cc from 'classcat';
import dayjs from 'dayjs';
import { expandCssShorthand } from 'utils/collection-manipulation/expand-css-shorthand.util';
import {
  ColSizes,
  EContainerIconSize,
  EIconSize
} from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { dateFormat } from 'utils/date-manipulation/date-format';
import { checkIsNotESportType } from 'utils/sportsbook/check-type-is-not-sportbook';
import { TPopover } from 'interfaces/betslip';
import { ETeamNumber } from 'interfaces/sportsbook';
import { Game, Market } from 'interfaces/sportsbook-data-levels';
import { EsportPageType } from 'newelements/Sportsbook/Esport/helpers/getGameTypeByPageType';
import { FavoriteButtonGame } from 'components/FavoriteButton';
import { GlobalIcon } from 'components/GlobalIcon';
import { MarketsCount } from 'components/MarketsCount';
import { Odds } from 'components/Odds';
import { PickerButton } from 'components/PickerButton';
import { TeamCaption } from 'components/TeamCaption';
import { GameTime } from 'newcomponents/Shared/GameTime';
import { MatchStatistic } from 'newcomponents/Shared/sportsbook/MatchStatistic';
import { NotificationIcon } from 'newcomponents/Shared/sportsbook/NotificationIcon';
import { VideoCamIcon } from 'newcomponents/Shared/sportsbook/VideoCamIcon';
import { Col } from 'newcomponents/UI/Col';
import { Row } from 'newcomponents/UI/Row';
import { useSearchParams } from 'hooks/useSearchParams';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { getOddFormat } from 'store/selectors/betslip';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type Props = {
  game: Game;
  currentSport: string;
  isLive: boolean;
  isOutRight: boolean;
  events: Array<{
    value: string;
    coeficient: number;
    handicap?: string;
  }>;
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  oddChangeHandler: Function;
  selectedOdd: string[];
  setMarketGroupID: Function;
  marketGroupID: number | null;
  hideWrapper?: boolean;
  popover: TPopover;
  hidePopover: () => void;
  competitionName?: string;
  regionAlias?: string;
};

export const DesktopESportMatchCardLine: FC<Props> = ({
  game,
  onClick,
  isLive,
  currentSport,
  isOutRight,
  events,
  oddChangeHandler,
  selectedOdd,
  setMarketGroupID,
  marketGroupID,
  hideWrapper,
  popover,
  hidePopover,
  competitionName,
  regionAlias
}) => {
  const {
    params: { sport }
  } = useSbRouteMatch();

  const oddFormat = useSelector(getOddFormat);

  const { type } = useSearchParams() as { type: EsportPageType };

  const markets = useMemo(
    () =>
      Object.values(game.market || {})
        .sort((a, b) => a.group_id - b.group_id)
        .reduce((result: Market[], market) => {
          const isGroupExist = !!result.find(
            ({ group_id }) => group_id === market.group_id
          );

          if (!isGroupExist) {
            result.push(market);
          }

          return result;
        }, []),
    [game.market]
  );

  const marketName = useMemo(() => {
    const group = markets.find(market => market.group_id === marketGroupID);

    return group?.group_name;
  }, [marketGroupID]);

  const menuItems =
    markets.length > 1 && markets.every(item => !!item.group_name)
      ? markets.map(({ group_id, group_name }) => ({
          key: group_id,
          label: group_name
        }))
      : false;

  const isPrematchEsportsAll = type === 'prematch' && sport === 'AllESports';

  return (
    <div
      data-testid="game"
      className={cc([
        'desktopESportMatchCardLine__container',
        {
          'desktopESportMatchCardLine__container--isLive':
            hideWrapper || isPrematchEsportsAll,
          'desktopESportMatchCardLine__container--eSportPrematch':
            isPrematchEsportsAll
        }
      ])}
      onClick={onClick}
    >
      <Row className="desktopESportMatchCardLine__header">
        <Row align="center">
          {isPrematchEsportsAll ? (
            <Col className="esport__header--region">
              <GlobalIcon
                lib="flags"
                name={regionAlias || ''}
                size={EIconSize._24}
                className="text-color"
              />
              <span className="esport__header--competition">
                {competitionName}
              </span>
            </Col>
          ) : (
            <>
              <Col
                style={expandCssShorthand({ padding: 0 })}
                className="esport__gameTime__container"
              >
                <GameTime
                  game={game}
                  isLive={isLive}
                  currentSport={currentSport}
                  dir={isLive ? 'row' : 'col'}
                  useGameMapName
                />
              </Col>
              <Col style={expandCssShorthand({ padding: '8px' })}>{`${
                game.text_info || ''
              } ${game.info?.add_info || ''}`}</Col>
            </>
          )}
        </Row>
        <Col
          className="esport__header-right desktopESportMatchCardLine__col"
          xs={ColSizes.SIZE_24}
          lg={ColSizes.SIZE_12}
        >
          {isPrematchEsportsAll && (
            <span className="esport__header--gameTime">
              {dayjs.unix(game.start_ts).format(
                dateFormat({
                  date: SpringConfigs.DT.shortDate,
                  time: SpringConfigs.DT.time,
                  dateTimeSeparator: ' | '
                })
              )}
            </span>
          )}
          {menuItems && (
            <Dropdown
              overlay={
                <Menu
                  selectable
                  selectedKeys={[String(marketGroupID)]}
                  items={menuItems}
                  onClick={menuItem => {
                    menuItem?.domEvent?.stopPropagation();
                    setMarketGroupID(+menuItem.key);
                  }}
                />
              }
              trigger={['hover']}
            >
              {marketName ? (
                <PickerButton>{marketName}</PickerButton>
              ) : (
                <SkeletonButton style={{ width: 65 }} />
              )}
            </Dropdown>
          )}
        </Col>
      </Row>
      <Row className="desktopESportMatchCardLine__row">
        <Col xs={ColSizes.SIZE_24} lg={ColSizes.SIZE_12}>
          <Row align="center">
            <Col xs={ColSizes.SIZE_24} lg={ColSizes.SIZE_17}>
              <Row style={{ minHeight: 68 }}>
                <Col
                  xs={ColSizes.SIZE_20}
                  className="desktopESportMatchCardLine__teams"
                >
                  <div className="desktopESportMatchCardLine__team">
                    <TeamCaption
                      size="md"
                      logo
                      name={game.team1_name}
                      teamId={game.team1_id}
                      teamNumber={ETeamNumber.HOME}
                      isNotESport={checkIsNotESportType(game)}
                      className="esport__team-name"
                    />
                    {isLive && (
                      <div className="desktopESportMatchCardLine__score">
                        {game?.info?.score1}
                      </div>
                    )}
                  </div>
                  {!isOutRight && (
                    <div className="desktopESportMatchCardLine__team">
                      <TeamCaption
                        size="md"
                        logo
                        name={game.team2_name}
                        teamId={game.team2_id}
                        teamNumber={ETeamNumber.AWAY}
                        isNotESport={checkIsNotESportType(game)}
                        className="esport__team-name"
                      />
                      {isLive && (
                        <div className="desktopESportMatchCardLine__score">
                          {game?.info?.score2}
                        </div>
                      )}
                    </div>
                  )}
                </Col>
                <Col
                  xs={ColSizes.SIZE_4}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <div className="desktopESportMatchCardLine__iconWrapper iconWrapperESport">
                    <div
                      className={cc([
                        'iconWrapperESport__videoCam',
                        {
                          iconWrapperESport__videoCam__noVideo:
                            !game.hasVideoStream
                        }
                      ])}
                    >
                      {game.hasVideoStream ? (
                        <VideoCamIcon
                          size={EIconSize._14}
                          iconContainerSize={EContainerIconSize.SMALL}
                        />
                      ) : SpringConfigs.PUSH_NOTIFICATIONS_ENABLED ? (
                        <NotificationIcon
                          game={game}
                          currentSport={currentSport}
                          size={EIconSize._16}
                          iconContainerSize={EContainerIconSize.SMALL}
                        />
                      ) : null}

                      <FavoriteButtonGame
                        size={EIconSize._16}
                        iconContainerSize={EContainerIconSize.SMALL}
                        game={{ id: game.id, type: game.type }}
                      />
                    </div>
                    <div className="iconWrapperESport__statistics__notifications">
                      {SpringConfigs.PUSH_NOTIFICATIONS_ENABLED &&
                      game.hasVideoStream ? (
                        <NotificationIcon
                          game={game}
                          currentSport={currentSport}
                          size={EIconSize._16}
                          iconContainerSize={EContainerIconSize.SMALL}
                        />
                      ) : null}
                      {SpringConfigs.STATISTICS_SWITCHER &&
                        game.is_stat_available && (
                          <div className="desktopESportMatchCardLine__statisticLinkWrapper">
                            <div className="desktopESportMatchCardLine__statisticLink">
                              <MatchStatistic
                                matchId={game.id}
                                size={EIconSize._16}
                                iconContainerSize={EContainerIconSize.SMALL}
                              />
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col xs={ColSizes.SIZE_24} lg={ColSizes.SIZE_12}>
          {!isOutRight && (
            <div className="flexbox justifyEnd">
              {events?.length ? (
                <Odds
                  size="large"
                  value={selectedOdd}
                  onChange={oddChangeHandler}
                  options={events}
                  popover={popover}
                  hidePopover={hidePopover}
                  isBlocked={game.is_blocked}
                  oddFormat={oddFormat}
                />
              ) : (
                <Odds
                  size="large"
                  options={[]}
                  popover={popover}
                  hidePopover={hidePopover}
                  isBlocked={game.is_blocked}
                  oddFormat={oddFormat}
                />
              )}
              <MarketsCount size="large">+{game.markets_count}</MarketsCount>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};
